import { CardGridWidget } from '../../widgets/CardGridWidget';
import styled from '@emotion/styled';
import * as React from 'react';
import { useState } from 'react';
import { useDeepLinking } from '../../hooks/useDeepLinking';
import { ContentWidget } from '../../widgets/ContentWidget';
import { PillButtonFrame } from '../../widgets/PillPrimitives';
import { SelectionPillWidget } from '../../widgets/SelectionPillWidget';
import { headingTypography } from '../../widgets/typography';
import { RoadmapCardWidget } from './RoadmapCardWidget';
import { useRoadmap } from './useRoadmap';
import { PageWidget } from '../../widgets/layout/PageWidget';

export const RoadmapPage: React.FC = () => {
  const { roadmap, error, reload } = useRoadmap();
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  useDeepLinking({ forwardRef: contentRef, selectors: ['article[id]'], linkOnClick: false });

  const activeGroup = roadmap?.groups.some((group) => group.id === selectedGroup) ? selectedGroup : null;
  const groups = roadmap?.groups.filter((group) => !activeGroup || group.id === activeGroup) ?? [];
  const renderContent = () => {
    if (error) {
      return (
        <S.Message role="alert">
          <S.MessageTitle>The roadmap is temporarily unavailable</S.MessageTitle>
          <S.MessageDescription>Please try again shortly.</S.MessageDescription>
          <S.RetryButton onClick={reload}>Try again</S.RetryButton>
        </S.Message>
      );
    }
    if (!roadmap) {
      return <S.Message role="status">Loading the roadmap…</S.Message>;
    }
    if (roadmap.groups.length === 0) {
      return (
        <S.Message role="status">
          <S.MessageTitle>More to come</S.MessageTitle>
          <S.MessageDescription>Check back soon for updates to our roadmap.</S.MessageDescription>
        </S.Message>
      );
    }
    return (
      <>
        <S.Filters aria-label="Filter by product">
          <SelectionPillWidget
            type="radio"
            name="roadmap-product"
            checked={!activeGroup}
            onChange={() => setSelectedGroup(null)}
          >
            All products
          </SelectionPillWidget>
          {roadmap.groups.map((group) => (
            <SelectionPillWidget
              key={group.id}
              type="radio"
              name="roadmap-product"
              checked={activeGroup === group.id}
              onChange={() => setSelectedGroup(group.id)}
            >
              {group.name}
              <S.Count>{group.cards.length}</S.Count>
            </SelectionPillWidget>
          ))}
        </S.Filters>
        {groups.map((group) => (
          <S.Group key={group.id} aria-labelledby={`roadmap-group-${group.id}`}>
            <S.GroupHeading>
              <S.GroupTitle id={`roadmap-group-${group.id}`}>{group.name}</S.GroupTitle>
              <S.GroupCount>
                {group.cards.length} {group.cards.length === 1 ? 'idea' : 'ideas'}
              </S.GroupCount>
            </S.GroupHeading>
            <S.Grid>
              {group.cards.map((card) => (
                <RoadmapCardWidget key={card.id} card={card} />
              ))}
            </S.Grid>
          </S.Group>
        ))}
      </>
    );
  };

  return (
    <PageWidget title="Platform roadmap" width={1200}>
      <S.Header>
        <h1>Platform roadmap</h1>
        <p>Explore what we’re considering, planning, and building across JourneyApps Platform.</p>
      </S.Header>
      <div ref={contentRef}>{renderContent()}</div>
    </PageWidget>
  );
};

namespace S {
  export const GroupTitle = styled.h2`
    ${(p) => headingTypography(p.theme)};
    font-size: 24px;
    font-weight: 600;
  `;
  export const GroupCount = styled.span`
    font-size: 12px;
  `;
  export const MessageTitle = styled.h2`
    ${(p) => headingTypography(p.theme)};
    font-size: 22px;
    font-weight: 600;
  `;
  export const MessageDescription = styled.p`
    margin: 14px 0 24px;
  `;
  export const Header = styled(ContentWidget)`
    margin-bottom: 30px;
  `;
  export const Filters = styled.nav`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    padding-bottom: 30px;
    border-bottom: 1px solid ${(p) => p.theme.surface.border};
  `;
  export const RetryButton = styled(PillButtonFrame)`
    gap: 10px;
    padding: 11px 16px;
    border: 1px solid ${(p) => p.theme.surface.border};
    color: ${(p) => p.theme.text.paragraph};
    background: ${(p) => p.theme.card.background};
    font-size: 13px;
    cursor: pointer;
    &:hover {
      background: ${(p) => p.theme.card.hoverBackground};
    }
    &:focus-visible {
      outline: 2px solid ${(p) => p.theme.text.special};
      outline-offset: 3px;
    }
  `;
  export const Count = styled.span`
    font-size: 11px;
    opacity: 0.75;
  `;
  export const Group = styled.section`
    margin-top: 36px;
  `;
  export const GroupHeading = styled.div`
    display: flex;
    align-items: baseline;
    gap: 14px;
    margin-bottom: 20px;
  `;
  export const Grid = styled(CardGridWidget)`
    align-items: start;
  `;
  export const Message = styled.div`
    padding: 48px 24px;
    border: 1px solid ${(p) => p.theme.surface.border};
    border-radius: 18px;
    text-align: center;
  `;
}
